import {nextTick, unref, type MaybeRef} from 'vue';
import {
    flatErrors,
    type InferRegleValidationResult,
    type RegleRoot,
    type RegleStatus,
    type SuperCompatibleRegleFieldStatus,
    type SuperCompatibleRegleRoot,
} from '@regle/core';
import {scrollToError, type ScrollToErrorOptions} from '../scrollToError';

export interface ValidateFormOptions extends ScrollToErrorOptions {}

/**
 * Validate a form and scroll to the first error if it exists.
 *
 * @param r$ - The Regle root instance.
 * @param [options] - The options for the validation.
 * @returns The validation result and the type safe data.
 *
 * @usage
 * ```ts
 * const {valid, data} = await validateForm(r$); // Validate the form and scroll to the first error if it exists.
 *
 * const {valid, data} = await validateForm(r$, {formId: 'my-form-id'}); // Scoped to a form container
 * ```
 *
 * @link https://reglejs.dev/core-concepts/type-safe-output
 *
 */
export async function validateForm<TRegle extends SuperCompatibleRegleRoot | SuperCompatibleRegleFieldStatus>(
    r$: MaybeRef<TRegle>,
    options?: ValidateFormOptions,
): Promise<InferRegleValidationResult<TRegle>> {
    const regle = unref(r$) as unknown as RegleRoot<any, any, any, any> | RegleStatus<any, any, any>;
    try {
        const {valid, data, errors, issues} = await regle.$validate();
        await nextTick();
        const isDomValid = await scrollToError(options);

        if (!valid || !isDomValid) {
            if (import.meta.dev || options?.debug) {
                if (!isDomValid) {
                    // eslint-disable-next-line no-console
                    console.info('Errors found in DOM');
                }
                // eslint-disable-next-line no-console
                console.info('Form validation failed', regle.$errors);
                // eslint-disable-next-line no-console
                console.debug('Errors:', flatErrors(regle.$errors, {includePath: true}));
            }
            return {valid: false, data, errors, issues} as InferRegleValidationResult<TRegle>;
        }

        return {valid: true, data, errors, issues} as InferRegleValidationResult<TRegle>;
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`Unexpected error in form validation ${options?.formId ? `(Form: ${options.formId})` : ''}`, error);
        return {valid: false, data: regle.$value, errors: regle.$errors, issues: regle.$issues} as InferRegleValidationResult<TRegle>;
    }
}
